import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Seo } from "../components/Seo";

export default function MembershipSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "checking", info: null });

  useEffect(() => {
    if (!sid) { setState({ status: "error", info: "Missing session id." }); return; }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/subscriptions/status/${sid}`);
        if (data.payment_status === "paid" || ["active", "trialing"].includes(data.subscription_status)) {
          await refresh();
          setState({ status: "paid", info: data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", info: data });
          return;
        }
      } catch (e) {/* keep polling */ console.error("Membership status poll error:", e); }
      if (attempts < 8) setTimeout(poll, 2000);
      else setState({ status: "timeout", info: null });
    };
    poll();
    return () => { cancelled = true; };
  }, [sid, refresh]);

  return (
    <div data-testid="membership-success-page" className="min-h-screen flex items-center justify-center px-6 pt-24">
      <Seo title="Membership" description="Membership confirmation." path="/membership/success" noindex />
      <div className="max-w-lg text-center">
        {state.status === "checking" && (
          <>
            <div className="overline">Finalising</div>
            <h1 className="font-display text-5xl mt-4">Holding your seat...</h1>
            <p className="text-white/60 mt-4">Confirming with Stripe.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <div className="overline">Welcome aboard</div>
            <h1 className="font-display text-6xl mt-4">You're in.</h1>
            <p className="text-white/70 mt-6">Membership active. The upload bay is unlocked.</p>
            <div className="mt-10 flex gap-4 justify-center">
              <Link to="/upload" data-testid="success-go-upload" className="btn-primary">Upload Your First Swing</Link>
              <Link to="/feed" className="btn-ghost">See the Feed</Link>
            </div>
          </>
        )}
        {state.status === "expired" && (
          <>
            <h1 className="font-display text-5xl">Session expired.</h1>
            <p className="text-white/60 mt-4">No problem — start a new checkout.</p>
            <Link to="/pricing" className="btn-primary mt-8 inline-block">Try Again</Link>
          </>
        )}
        {state.status === "timeout" && (
          <>
            <h1 className="font-display text-5xl">Still processing...</h1>
            <p className="text-white/60 mt-4">Refresh in a moment, or contact us if it persists.</p>
            <Link to="/feed" className="btn-ghost mt-8 inline-block">Back to Feed</Link>
          </>
        )}
        {state.status === "error" && (
          <>
            <h1 className="font-display text-5xl">Something went wrong.</h1>
            <p className="text-white/60 mt-4">{state.info}</p>
            <Link to="/pricing" className="btn-primary mt-8 inline-block">Back to Pricing</Link>
          </>
        )}
      </div>
    </div>
  );
}
